import { Close } from '@material-ui/icons';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Wallet } from 'src/types/wallet';
import { useCompositionColors } from 'src/utils/hooks/useChartColors';
import { intlFormatNumber } from 'src/utils/number';

import { CompositionItem } from '../CompositionItem';

type ShowCompositionProps = {
  wallet?: Wallet | undefined;
  compositions?: Array<{
    _key: string;
    quotasAmount: number | undefined;
    value: number;
    color?: string;
  }>;
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
};

export function ShowComposition({
  wallet,
  compositions,
  open,
  onClose,
  onFinish,
}: ShowCompositionProps) {
  const chartColors = useCompositionColors(compositions);

  const tableHeaders = {
    ativoLastro: 'Ativo Lastro',
    cotas: '#Fundos',
    percentualTotal: '% Total',
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
        <Typography
          variant="h5"
          align="center"
          style={{ marginBottom: 32, marginTop: 8 }}>
          Composição da carteira
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
          <Box
            sx={{
              width: '50%',
              margin: '0 auto',
              mb: 2,
            }}>
            {compositions?.map((comp, idx) => (
              <CompositionItem
                color={chartColors[idx]}
                key={comp._key}
                _key={comp._key}
                value={comp.value}
              />
            ))}
          </Box>
          <Box width="100%" height={140}>
            {compositions && (
              <Doughnut
                data={{
                  labels: compositions.map((comp) => comp._key),
                  datasets: [
                    {
                      data: compositions.map((comp) =>
                        typeof comp.value === 'number'
                          ? comp.value
                          : Number(String(comp.value).replace('%', '')),
                      ),
                      backgroundColor: chartColors,
                    },
                  ],
                }}
                options={{
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed;
                          return `${label}: ${intlFormatNumber(value)}%`;
                        },
                      },
                    },
                  },
                  cutout: '70%',
                  responsive: true,
                  maintainAspectRatio: false,
                }}
              />
            )}
          </Box>
          <Box width="100%">
            <TableContainer component={Box} sx={{ maxHeight: 300 }}>
              <Table size="medium">
                <TableHead sx={{ backgroundColor: '#FF821046' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {tableHeaders.ativoLastro}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      {tableHeaders.cotas}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      {tableHeaders.percentualTotal}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {compositions?.map((composition) => (
                    <TableRow key={composition._key}>
                      <TableCell component="th" scope="row">
                        {composition._key}
                      </TableCell>
                      <TableCell align="right">
                        {composition.quotasAmount}
                      </TableCell>
                      <TableCell align="right">
                        {intlFormatNumber(composition.value)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
